import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, X, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { startLogin } from "@/lib/auth";
import { openCheckout, paddleEnabled, paddlePublic } from "@/lib/paddle";

/** Public pricing page.
 *  Checkout CTAs activate only when `REACT_APP_PADDLE_CLIENT_TOKEN` is set.
 *  Each paid plan maps to its Paddle `priceId` via the env vars configured
 *  per environment (sandbox vs. production). */
const PLANS = [
  { id: "free",    price_eur: "0",     cadence: "",         popular: false, priceEnv: null },
  { id: "singolo", price_eur: "9,90",  cadence: "one_off",  popular: false, priceEnv: "REACT_APP_PADDLE_PRICE_SINGLE" },
  { id: "basic",   price_eur: "23,90", cadence: "monthly",  popular: true,  priceEnv: "REACT_APP_PADDLE_PRICE_BASIC" },
  { id: "pro",     price_eur: "48,90", cadence: "monthly",  popular: false, priceEnv: "REACT_APP_PADDLE_PRICE_PRO" },
];

const FEATURES = [
  "credits", "preview_mp3", "wav", "pdf", "chat", "ab_compare", "rollover", "post_cancel",
];

// 1-credit = 1-workflow (analysis + mastering + preview_mp3 allowance bundled).
// Numbers match the `mastering` count in backend `plan_limits.py` (analyses
// equals mastering by design for buyer-protection safety).
const FEATURE_MATRIX = {
  free:    { credits: "1", preview_mp3: "2", wav: false, pdf: false, chat: "2", ab_compare: false, rollover: "·", post_cancel: "·" },
  singolo: { credits: "1", preview_mp3: "3", wav: true, pdf: true, chat: "5", ab_compare: false, rollover: "·", post_cancel: "·" },
  basic:   { credits: "8/m", preview_mp3: "24/m", wav: true, pdf: true, chat: "50/m", ab_compare: true, rollover: "24 · 60g", post_cancel: "60g" },
  pro:     { credits: "20/m", preview_mp3: "60/m", wav: true, pdf: true, chat: "120/m", ab_compare: true, rollover: "60 · 60g", post_cancel: "60g" },
};

function Cell({ value, t }) {
  if (value === true) return <Check className="w-4 h-4 text-emerald-400 mx-auto" />;
  if (value === false) return <X className="w-4 h-4 text-white/20 mx-auto" />;
  const display = typeof value === "string" && value.endsWith("/m")
    ? `${value.slice(0, -2)}${t("pricing.per_month_suffix")}`
    : value;
  return <span className="tabular-nums text-sm text-white">{display}</span>;
}

export default function Pricing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  // Checkout is "visible" only when (a) the Paddle client token is wired,
  // AND (b) either the public flag is on OR the current user is admin.
  // During the sandbox phase we keep this admin-only so real visitors
  // never land on a sandbox-only checkout that rejects real cards.
  const paddleReady = paddleEnabled() && (paddlePublic() || Boolean(user?.is_admin));

  const handleCheckout = async (plan) => {
    const priceId = (process.env[plan.priceEnv] || "").trim();
    if (!priceId) {
      toast.error(t("pricing.checkout_error"));
      return;
    }
    // Not signed in → bounce through WorkOS login first (user comes back
    // to /pricing and can click again). This matches the rest of the app.
    if (!user) {
      try { await startLogin(); } catch { /* ignore */ }
      return;
    }
    setLoadingPlan(plan.id);
    try {
      await openCheckout({
        priceId,
        plan: plan.id,
        email: user.email,
        userId: user.user_id,
        discountCode: (new URLSearchParams(window.location.search).get("discount") || "").trim().toUpperCase() || undefined,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error(t("pricing.checkout_error"));
    } finally {
      // Checkout.open() returns immediately; the overlay stays open until
      // the user closes it. Clear the spinner after a brief delay so the
      // button isn't frozen if they dismiss the overlay.
      setTimeout(() => setLoadingPlan(null), 1500);
    }
  };

  const ctaFor = (plan) => {
    if (plan.id === "free") {
      return {
        label: user ? t("pricing.cta_free_current") : t("pricing.cta_free_get"),
        onClick: user ? null : () => startLogin(),
        disabled: Boolean(user),
      };
    }
    if (!paddleReady) {
      return { label: t("pricing.cta_coming_soon"), onClick: null, disabled: true };
    }
    if (!user) {
      return { label: t("pricing.cta_login_to_buy"), onClick: () => handleCheckout(plan), disabled: false };
    }
    const label = loadingPlan === plan.id
      ? t("pricing.cta_processing")
      : t(`pricing.cta_${plan.id === "singolo" ? "buy_singolo" : plan.id === "basic" ? "subscribe_basic" : "subscribe_pro"}`);
    return { label, onClick: () => handleCheckout(plan), disabled: loadingPlan === plan.id };
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)] text-white">
      <Header />
      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
        <div className="text-center mb-10" data-testid="pricing-hero">
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight mb-3">
            {t("pricing.title")}
          </h1>
          <p className="text-base text-[color:var(--fg-muted)] max-w-2xl mx-auto">
            {t("pricing.subtitle")}
          </p>
          <p data-testid="pricing-currency-notice" className="mt-4 text-xs text-[color:var(--fg-muted)] max-w-xl mx-auto">
            {t("pricing.currency_notice")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {PLANS.map((p) => {
            const cta = ctaFor(p);
            const isLoading = loadingPlan === p.id;
            const isPrimary = p.popular;
            return (
              <div
                key={p.id}
                data-testid={`pricing-card-${p.id}`}
                className={`rounded-2xl border p-6 flex flex-col ${
                  isPrimary
                    ? "border-[color:var(--brand)]/50 bg-[color:var(--brand)]/5"
                    : "border-white/10 bg-white/[0.02]"
                }`}
              >
                {isPrimary && (
                  <span className="tiny-caps text-[10px] text-[color:var(--brand)] mb-2">
                    {t("pricing.popular")}
                  </span>
                )}
                <h3 className="text-xl font-semibold mb-1">{t(`pricing.plan_${p.id}_name`)}</h3>
                <p className="text-xs text-[color:var(--fg-muted)] mb-4">
                  {t(`pricing.plan_${p.id}_tagline`)}
                </p>
                <div className="mb-4">
                  <span className="text-4xl font-display font-bold">€{p.price_eur}</span>
                  {p.cadence && (
                    <span className="text-sm text-[color:var(--fg-muted)] ml-1">
                      {t(`pricing.cadence_${p.cadence}`)}
                    </span>
                  )}
                </div>
                <ul className="text-sm text-white/80 space-y-2 mb-6 flex-1">
                  {FEATURES.map((f) => {
                    const val = FEATURE_MATRIX[p.id][f];
                    // "·" is treated the same as `false` here — plan has no
                    // access to that feature. Only strings different from
                    // "·" and the literal `true` get the green check.
                    const notAvailable = val === false || val === "·";
                    return (
                      <li key={f} className="flex items-start gap-2">
                        {notAvailable ? (
                          <X className="w-4 h-4 text-white/20 shrink-0 mt-0.5" />
                        ) : (
                          <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                        )}
                        <span>
                          {/* Free plan: show "1 trial analysis (no mastering)"
                              instead of the universal "1 credit = 1 analysis + 1
                              mastering" since Free doesn't include mastering. */}
                          {f === "credits" && p.id === "free"
                            ? t("pricing.feature_credits_free")
                            : t(`pricing.feature_${f}`)}
                          {typeof val === "string" && val !== "·" &&
                            `: ${
                              val.endsWith("/m")
                                ? `${val.slice(0, -2)}${t("pricing.per_month_suffix")}`
                                : val
                            }`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <button
                  type="button"
                  disabled={cta.disabled}
                  onClick={cta.onClick || undefined}
                  data-testid={`pricing-cta-${p.id}`}
                  aria-disabled={cta.disabled}
                  className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                    cta.disabled
                      ? "bg-white/10 text-white/60 cursor-not-allowed"
                      : isPrimary
                        ? "bg-[color:var(--brand)] text-black hover:bg-[color:var(--brand)]/90"
                        : "bg-white text-black hover:bg-white/90"
                  }`}
                >
                  {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {cta.label}
                </button>
              </div>
            );
          })}
        </div>

        {paddleReady && (
          <p className="text-center text-xs text-[color:var(--fg-muted)] mb-10" data-testid="pricing-payment-hint">
            {t("pricing.payment_hint")}
          </p>
        )}

        <div className="rounded-2xl border border-white/10 overflow-x-auto mb-10 -mx-6 lg:mx-0 lg:rounded-2xl">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-white/5 text-[10px] tiny-caps text-[color:var(--fg-muted)]">
              <tr>
                <th className="text-left px-3 sm:px-4 py-3 sticky left-0 bg-[color:var(--bg-elev)]">{t("pricing.matrix_feature")}</th>
                {PLANS.map((p) => (
                  <th key={p.id} className="text-center px-3 sm:px-4 py-3">
                    {t(`pricing.plan_${p.id}_name`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {FEATURES.map((f) => (
                <tr key={f}>
                  <td className="px-3 sm:px-4 py-3 text-white/80 sticky left-0 bg-[color:var(--bg-base)] text-xs sm:text-sm">{t(`pricing.feature_${f}`)}</td>
                  {PLANS.map((p) => (
                    <td key={p.id} className="px-3 sm:px-4 py-3 text-center whitespace-nowrap">
                      <Cell value={FEATURE_MATRIX[p.id][f]} t={t} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-center text-xs text-[color:var(--fg-muted)]">
          {t("pricing.footer_note")} ·{" "}
          <Link to="/legal/terms" className="underline hover:text-white">
            {t("pricing.footer_terms")}
          </Link>
        </p>
      </main>
    </div>
  );
}
